import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LogOut, Copy, Share2, DollarSign, FileText, ExternalLink, Handshake, ChevronLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { portalFetch, portalLogout, getPortalToken } from '../lib/portalAuth';

const f$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

const STATUS_STYLE = {
  new: 'bg-blue-100 text-blue-800',
  pending: 'bg-amber-100 text-amber-800',
  in_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  converted: 'bg-emerald-100 text-emerald-800',
  paid: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
};

export default function BrokerDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [commissions, setCommissions] = useState({ commissions: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!getPortalToken('broker')) { navigate('/broker/login'); return; }
    Promise.all([
      portalFetch('broker', '/api/portals/broker/me'),
      portalFetch('broker', '/api/portals/broker/submissions'),
      portalFetch('broker', '/api/portals/broker/commissions'),
    ]).then(([m, s, c]) => {
      setMe(m);
      setSubmissions(s.submissions || []);
      setCommissions(c);
      setLoading(false);
    }).catch(err => {
      setError(err.message);
      setLoading(false);
      if (err.message.includes('expired')) navigate('/broker/login');
    });
  }, [navigate]);

  const logout = async () => { await portalLogout('broker'); navigate('/broker/login'); };
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied to clipboard'); };

  if (loading) return <Center>Loading…</Center>;
  if (error) return <Center>{error}</Center>;
  if (!me) return null;

  const totals = commissions.totals || {};
  const recentSubs = submissions.slice(0, 8);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="broker-dashboard">
      {/* Header */}
      <div className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-5 py-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/" className="inline-flex items-center text-[10px] uppercase tracking-widest text-gray-400 hover:text-white mb-1">
              <ChevronLeft className="h-3 w-3 mr-1" /> Back to ACE
            </Link>
            <p className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">ACE · Broker Portal</p>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight mt-1">{me.profile?.partner_name || 'Broker'}</h1>
            <p className="text-xs text-gray-400 mt-1">Broker code: <span className="font-mono">{me.session.broker_code}</span> · {me.session.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/collective" data-testid="broker-collective-link">
              <Button variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:bg-gray-800">
                <Handshake className="h-3 w-3 mr-1" /> Driven Collective
              </Button>
            </Link>
            <Button onClick={logout} variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="broker-logout">
              <LogOut className="h-3 w-3 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 -mt-3 pb-12 space-y-5">
        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Pending $" value={f$(totals.pending)} color="amber" testid="kpi-pending" />
          <Kpi label="Paid Lifetime" value={f$(totals.paid)} color="emerald" testid="kpi-paid" />
          <Kpi label="Lifetime Earn" value={f$(totals.lifetime)} color="red" testid="kpi-lifetime" />
          <Kpi label="Submissions" value={submissions.length} color="blue" testid="kpi-submissions" />
        </div>

        {/* Referral link generator */}
        <Card className="p-5 bg-gradient-to-br from-red-950 to-black text-white border-red-900" data-testid="referral-card">
          <div className="flex items-start gap-3 mb-4">
            <Sparkles className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
            <div>
              <h2 className="font-bold text-lg">Your trackable referral links</h2>
              <p className="text-xs text-gray-300 mt-1">Share any of these — every submission from your link is automatically tagged with your commission code.</p>
            </div>
          </div>
          <div className="space-y-2">
            {(me.referral_links || []).map(l => (
              <div key={l.label} className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-red-400 font-bold">{l.label}</p>
                  <p className="text-xs text-gray-200 font-mono truncate">{l.url}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => copy(l.url)} className="px-2 py-1 text-[10px] font-semibold bg-white/10 hover:bg-white/20 rounded" data-testid={`copy-${l.label.replace(/\s/g, '-')}`}>
                    <Copy className="h-3 w-3 inline" />
                  </button>
                  <a href={l.url} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-[10px] font-semibold bg-white/10 hover:bg-white/20 rounded">
                    <ExternalLink className="h-3 w-3 inline" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent submissions */}
        <Card className="p-5" data-testid="submissions-card">
          <h2 className="font-bold mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-red-600" /> Recent Submissions</h2>
          {recentSubs.length === 0 ? (
            <p className="text-xs text-gray-400 italic text-center py-8 border border-dashed rounded">No submissions yet. Share your referral link to start.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                  <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Client</th><th className="text-right px-3 py-2">Status</th></tr>
                </thead>
                <tbody>
                  {recentSubs.map(s => (
                    <tr key={s.submission_id} className="border-t">
                      <td className="px-3 py-2 text-xs">{fDate(s.created_at)}</td>
                      <td className="px-3 py-2 text-xs">{s.label}</td>
                      <td className="px-3 py-2">{s.raw?.submitted_client_name || s.client_name}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[s.quote_status || s.status] || STATUS_STYLE.new}`}>
                          {(s.quote_status || s.status || 'new').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Commissions */}
        <Card className="p-5" data-testid="commissions-card">
          <h2 className="font-bold mb-3 flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-700" /> Commissions</h2>
          {commissions.commissions.length === 0 ? (
            <p className="text-xs text-gray-400 italic text-center py-8 border border-dashed rounded">No commissions yet. They appear here once your referrals book.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                  <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Client</th><th className="text-left px-3 py-2">Service</th><th className="text-right px-3 py-2">Amount</th><th className="text-right px-3 py-2">Status</th></tr>
                </thead>
                <tbody>
                  {commissions.commissions.map(c => (
                    <tr key={c.commission_id} className="border-t">
                      <td className="px-3 py-2 text-xs">{fDate(c.created_at)}</td>
                      <td className="px-3 py-2">{c.client_name}</td>
                      <td className="px-3 py-2 text-xs capitalize">{c.service_type}</td>
                      <td className="px-3 py-2 text-right font-bold">{f$(c.amount)}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[c.status] || STATUS_STYLE.pending}`}>
                          {(c.status || 'pending').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="text-[11px] text-gray-400 text-center mt-2">
          Need help? Email{' '}<a href="mailto:partners@yourautoconcierge.com" className="text-red-600 hover:underline">partners@yourautoconcierge.com</a> · Commissions paid net 7 after the booking is fully paid.
        </p>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, color, testid }) => {
  const map = { red: 'border-red-200 text-red-700', amber: 'border-amber-200 text-amber-800', emerald: 'border-emerald-200 text-emerald-800', blue: 'border-blue-200 text-blue-700' };
  return (
    <Card className={`p-4 border ${map[color]}`} data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">{label}</p>
      <p className="text-2xl font-black mt-1">{value}</p>
    </Card>
  );
};

const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">{children}</div>
);
